/**
 * VialArgumentsSlide - Gallery slide showing a single Colagea vial
 * with product argument text overlays as white pills for readability.
 * Full background image with 4 white pill overlays at corners.
 */

export function VialArgumentsSlide() {
  return (
    <div className="w-full h-full relative overflow-hidden">
      {/* Full background image */}
      <img 
        src="https://d2xsxph8kpxj0f.cloudfront.net/310519663243023818/kXYViC6LoVarhABtxw9dmA/colagea-single-vial_c515d8d6.jpg"
        alt="Colagea Micro-Infuse Vial"
        className="w-full h-full object-cover"
      />

      {/* SVG connecting lines from center to pills */}
      <svg 
        className="absolute inset-0 w-full h-full z-10 pointer-events-none"
        viewBox="0 0 600 600"
        preserveAspectRatio="xMidYMid meet"
      >
        <line x1="280" y1="250" x2="120" y2="100" stroke="#0066CC" strokeWidth="1.2" strokeDasharray="5 4" opacity="0.4" />
        <circle cx="120" cy="100" r="3.5" fill="#0066CC" opacity="0.6" />
        <line x1="280" y1="350" x2="120" y2="490" stroke="#0066CC" strokeWidth="1.2" strokeDasharray="5 4" opacity="0.4" />
        <circle cx="120" cy="490" r="3.5" fill="#0066CC" opacity="0.6" />
        <line x1="320" y1="250" x2="480" y2="100" stroke="#0066CC" strokeWidth="1.2" strokeDasharray="5 4" opacity="0.4" />
        <circle cx="480" cy="100" r="3.5" fill="#0066CC" opacity="0.6" />
        <line x1="320" y1="350" x2="480" y2="490" stroke="#0066CC" strokeWidth="1.2" strokeDasharray="5 4" opacity="0.4" />
        <circle cx="480" cy="490" r="3.5" fill="#0066CC" opacity="0.6" />
      </svg>

      {/* Top-left pill: Visible Results Fast */}
      <div className="absolute top-[6%] left-[3%] lg:left-[5%] max-w-[150px] lg:max-w-[190px] z-20 animate-[fadeSlideIn_0.4s_ease-out_0.1s_both]">
        <div className="bg-white/95 backdrop-blur-sm rounded-xl px-3 py-2.5 lg:px-4 lg:py-3 shadow-lg border border-gray-100/50">
          <div className="flex items-start gap-2">
            <div className="w-7 h-7 lg:w-8 lg:h-8 rounded-full bg-[#e8f0fe] flex items-center justify-center flex-shrink-0 mt-0.5">
              <svg className="w-3.5 h-3.5 lg:w-4 lg:h-4 text-[#0066CC]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M13 10V3L4 14h7v7l9-11h-7z"/>
              </svg>
            </div>
            <div>
              <p className="text-[11px] lg:text-sm font-bold text-gray-900 leading-tight">Visible Results Fast</p>
              <p className="text-[9px] lg:text-xs text-gray-500 leading-snug mt-0.5">Denser hair from the first applications.</p>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom-left pill: Professional Care at Home */}
      <div className="absolute bottom-[6%] left-[3%] lg:left-[5%] max-w-[150px] lg:max-w-[190px] z-20 animate-[fadeSlideIn_0.4s_ease-out_0.25s_both]">
        <div className="bg-white/95 backdrop-blur-sm rounded-xl px-3 py-2.5 lg:px-4 lg:py-3 shadow-lg border border-gray-100/50">
          <div className="flex items-start gap-2">
            <div className="w-7 h-7 lg:w-8 lg:h-8 rounded-full bg-[#e8f0fe] flex items-center justify-center flex-shrink-0 mt-0.5">
              <svg className="w-3.5 h-3.5 lg:w-4 lg:h-4 text-[#0066CC]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"/>
              </svg>
            </div>
            <div>
              <p className="text-[11px] lg:text-sm font-bold text-gray-900 leading-tight">Professional at Home</p>
              <p className="text-[9px] lg:text-xs text-gray-500 leading-snug mt-0.5">Clinical-grade quality, no appointments.</p>
            </div>
          </div>
        </div>
      </div>

      {/* Top-right pill: Clinically Proven */}
      <div className="absolute top-[6%] right-[3%] lg:right-[5%] max-w-[150px] lg:max-w-[190px] z-20 animate-[fadeSlideIn_0.4s_ease-out_0.15s_both]">
        <div className="bg-white/95 backdrop-blur-sm rounded-xl px-3 py-2.5 lg:px-4 lg:py-3 shadow-lg border border-gray-100/50">
          <div className="flex items-start gap-2">
            <div className="w-7 h-7 lg:w-8 lg:h-8 rounded-full bg-[#e8f0fe] flex items-center justify-center flex-shrink-0 mt-0.5">
              <svg className="w-3.5 h-3.5 lg:w-4 lg:h-4 text-[#0066CC]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"/>
              </svg>
            </div>
            <div>
              <p className="text-[11px] lg:text-sm font-bold text-gray-900 leading-tight">Clinically Proven</p>
              <p className="text-[9px] lg:text-xs text-gray-500 leading-snug mt-0.5">Dermatologist-tested for safe results.</p>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom-right pill: Advanced Technology */}
      <div className="absolute bottom-[6%] right-[3%] lg:right-[5%] max-w-[150px] lg:max-w-[190px] z-20 animate-[fadeSlideIn_0.4s_ease-out_0.3s_both]">
        <div className="bg-white/95 backdrop-blur-sm rounded-xl px-3 py-2.5 lg:px-4 lg:py-3 shadow-lg border border-gray-100/50">
          <div className="flex items-start gap-2">
            <div className="w-7 h-7 lg:w-8 lg:h-8 rounded-full bg-[#e8f0fe] flex items-center justify-center flex-shrink-0 mt-0.5">
              <svg className="w-3.5 h-3.5 lg:w-4 lg:h-4 text-[#0066CC]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z"/>
              </svg>
            </div>
            <div>
              <p className="text-[11px] lg:text-sm font-bold text-gray-900 leading-tight">3x Deeper Penetration</p>
              <p className="text-[9px] lg:text-xs text-gray-500 leading-snug mt-0.5">Micro-Infuse targets the follicle directly.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * VialArgumentsThumbnail - Compact thumbnail for the gallery strip
 */
export function VialArgumentsThumbnail() {
  return (
    <div className="w-full h-full relative overflow-hidden">
      <img 
        src="https://d2xsxph8kpxj0f.cloudfront.net/310519663243023818/kXYViC6LoVarhABtxw9dmA/colagea-single-vial_c515d8d6.jpg"
        alt="Product Arguments"
        className="w-full h-full object-cover"
      />
      <div className="absolute bottom-0 right-0 bg-white/90 rounded-tl-lg px-1.5 py-1">
        <span className="text-[7px] font-semibold text-[#0066CC]">why it works</span>
      </div>
    </div>
  );
}

export default VialArgumentsSlide;
